import type {
  AutomationCaseHitDto,
  AutomationCaseRefDto,
  AutomationLinksFromDescriptionsDto,
  AutomationTestSearchDto,
  CaseAutomationDto,
} from '@tms/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api-client';
import { queryKeys } from '../../../lib/query-keys';

/**
 * Which autotest covers which test case: links picked in TMS or written in the
 * autotest code as @TmsLink and found by the scanner.
 */

const caseKeyOf = (caseKey: string) => ['automation', 'case-links', caseKey] as const;

export const useCaseAutomation = (caseKey: string) =>
  useQuery({
    queryKey: caseKeyOf(caseKey),
    queryFn: ({ signal }) =>
      api.get<CaseAutomationDto>(
        `/automation/cases/${encodeURIComponent(caseKey)}/links`,
        undefined,
        signal,
      ),
  });

/** A link changes the case's «automated» flag too, so the case itself is re-read. */
const useInvalidateCase = (caseKey: string) => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: caseKeyOf(caseKey) });
    void queryClient.invalidateQueries({ queryKey: ['test-cases'] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.automation.all });
  };
};

export const useAddCaseLink = (caseKey: string) => {
  const invalidate = useInvalidateCase(caseKey);
  return useMutation({
    mutationFn: (input: {
      automationProjectId: string;
      className: string;
      testMethod: string | null;
    }) =>
      api.post<CaseAutomationDto>(`/automation/cases/${encodeURIComponent(caseKey)}/links`, input),
    onSuccess: invalidate,
  });
};

export const useRemoveCaseLink = (caseKey: string) => {
  const invalidate = useInvalidateCase(caseKey);
  return useMutation({
    mutationFn: (linkId: string) =>
      api.delete<CaseAutomationDto>(
        `/automation/cases/${encodeURIComponent(caseKey)}/links/${encodeURIComponent(linkId)}`,
      ),
    onSuccess: invalidate,
  });
};

/** The search answers from two letters on. */
const MIN_QUERY = 2;

/** Classes and tests of every scanned project by words of their names — «Link an autotest». */
export const useTestSearch = (query: string, enabled: boolean) =>
  useQuery({
    queryKey: ['automation', 'test-search', query] as const,
    queryFn: ({ signal }) =>
      api.get<AutomationTestSearchDto>('/automation/tests/search', { q: query }, signal),
    enabled: enabled && query.length >= MIN_QUERY,
    placeholderData: keepPreviousData,
  });

/** Test cases of every project by key or title — «Link to a case» in the catalogue. */
export const useCaseSearch = (query: string, enabled: boolean) =>
  useQuery({
    queryKey: ['automation', 'case-search', query] as const,
    queryFn: ({ signal }) =>
      api.get<AutomationCaseHitDto[]>('/automation/case-search', { q: query }, signal),
    enabled: enabled && query.length >= MIN_QUERY,
    placeholderData: keepPreviousData,
  });

/** A link made from the autotest side: the case is chosen, not fixed. */
export const useLinkToCase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseKey,
      ...input
    }: {
      caseKey: string;
      automationProjectId: string;
      className: string;
      testMethod: string | null;
    }) =>
      api.post<CaseAutomationDto>(`/automation/cases/${encodeURIComponent(caseKey)}/links`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.automation.all });
      void queryClient.invalidateQueries({ queryKey: ['test-cases'] });
    },
  });
};

/** The cases linked to a project's classes and tests. */
export const useProjectCaseRefs = (projectId: string) =>
  useQuery({
    queryKey: queryKeys.automation.projectData(projectId, 'case-links'),
    queryFn: ({ signal }) =>
      api.get<AutomationCaseRefDto[]>(
        `/automation/projects/${encodeURIComponent(projectId)}/case-links`,
        undefined,
        signal,
      ),
    enabled: Boolean(projectId),
  });

/** «Автотесты в GitLab» lines of case descriptions → links; a dry run unless apply. */
export const useLinksFromDescriptions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apply: boolean) =>
      api.post<AutomationLinksFromDescriptionsDto>('/automation/case-links/from-descriptions', {
        apply,
      }),
    onSuccess: (result) => {
      if (result.applied) {
        void queryClient.invalidateQueries({ queryKey: ['automation', 'case-links'] });
        void queryClient.invalidateQueries({ queryKey: ['test-cases'] });
      }
    },
  });
};
